import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@shared/config/routes';
import { AlertTriangle, HelpCircle, Users, ShieldAlert, Activity, Menu } from 'lucide-react';
import { supabase } from '@shared/supabaseClient';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@shared/components/ui/dialog';
import { Button } from '@shared/components/ui/button';
import { fetchPublicBranding } from '@shared/services/brandingService';

/**
 * Fixed header bar for Depot Induction App
 * Shows company logo, site status, and on-site count
 */
const DEFAULT_LOGO = '/assets/atc-logo.png';

const DepotHeader = ({ user, currentOccupancy = 0, openIncidentsCount = 0, onLogout, onHelpClick, onMenuClick }) => {
  const navigate = useNavigate();
  const [siteStatus, setSiteStatus] = useState('safe'); // safe or hazard
  const [companyLogo, setCompanyLogo] = useState(DEFAULT_LOGO);
  const [logoSize, setLogoSize] = useState(80);
  const [depotName, setDepotName] = useState('Depot Induction System');
  const [latestHazard, setLatestHazard] = useState(null);
  const [totalHazardCount, setTotalHazardCount] = useState(0);
  const [allHazards, setAllHazards] = useState([]);
  const [showIncidentsModal, setShowIncidentsModal] = useState(false);

  // Fetch site status based on open incidents (medium and high only)
  useEffect(() => {
    const checkSiteStatus = async () => {
      try {
        // Check for medium/high severity incidents
        const { data: hazardIncidents, error, count } = await supabase
          .from('incidents')
          .select('*', { count: 'exact' })
          .in('status', ['open', 'investigating'])
          .in('severity', ['medium', 'high', 'critical'])
          .order('created_at', { ascending: false });

        if (!error && hazardIncidents && hazardIncidents.length > 0) {
          // Hazard reported
          setSiteStatus('hazard');
          setLatestHazard(hazardIncidents[0]); // Only the first/latest
          setTotalHazardCount(count || hazardIncidents.length);
          setAllHazards(hazardIncidents); // Store all hazards for modal
        } else {
          // No hazards
          setSiteStatus('safe');
          setLatestHazard(null);
          setTotalHazardCount(0);
          setAllHazards([]);
        }
      } catch (error) {
        console.error('Error checking site status:', error);
      }
    };

    checkSiteStatus();

    // Refresh every 30 seconds
    const interval = setInterval(checkSiteStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  // Load company logo from app_settings (branding service)
  useEffect(() => {
    const loadSettings = () => {
      try {
        const savedDepotName = localStorage.getItem('depotName');
        if (savedDepotName) {
          setDepotName(savedDepotName);
        }
      } catch (error) {
        console.error('Error loading logo/settings:', error);
      }
    };

    const loadFromLocalStorage = () => {
      loadSettings();
      const savedUrl = localStorage.getItem('company_logo_url');
      const savedSize = parseInt(localStorage.getItem('company_logo_size') || '80');
      setCompanyLogo(savedUrl || DEFAULT_LOGO);
      setLogoSize(savedSize);
    };

    const loadBranding = async () => {
      loadSettings();
      const branding = await fetchPublicBranding();
      if (branding?.logoUrl) {
        setCompanyLogo(branding.logoUrl);
      }
      if (branding?.logoSize) {
        setLogoSize(branding.logoSize);
      }
    };

    // Initial load from DB/localStorage
    loadBranding();

    // Listen for settings updates — read directly from localStorage for instant update
    const handleSettingsUpdate = () => {
      loadFromLocalStorage();
    };

    window.addEventListener('settingsUpdated', handleSettingsUpdate);
    return () => window.removeEventListener('settingsUpdated', handleSettingsUpdate);
  }, []);

  const getStatusColor = () => {
    return siteStatus === 'hazard' ? 'bg-red-600' : 'bg-green-600';
  };

  const getStatusText = () => {
    return siteStatus === 'hazard' ? 'Hazard Reported' : 'No Hazards';
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return 'bg-purple-600';
      case 'high':
        return 'bg-red-600';
      case 'medium':
        return 'bg-orange-500';
      case 'low':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-500';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <>
      {/* ── Compact stats topbar — in-flow, Axis-style ── */}
      <header className="dark shrink-0 border-b border-border bg-card px-4" style={{ height: '94px' }}>
        <div className="flex items-center gap-2 h-full">
          {/* Hamburger — mobile only */}
          <button
            onClick={onMenuClick}
            className="md:hidden p-1.5 rounded hover:bg-secondary transition-colors flex-shrink-0"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5 text-foreground" />
          </button>

          {/* Company Logo — uses logoSize from settings, no max cap */}
          <div className="flex-shrink-0 flex items-center h-full" style={{ marginLeft: '24px', paddingTop: '6px', paddingBottom: '6px' }}>
            {companyLogo ? (
              <img
                src={companyLogo}
                alt="Company Logo"
                style={{
                  height: `${Math.max(logoSize || 80, 40)}px`,
                  width: 'auto',
                  objectFit: 'contain',
                  objectPosition: 'left center',
                  display: 'block'
                }}
              />
            ) : (
              <div
                className="rounded-lg flex items-center justify-center text-lg font-bold px-6"
                style={{
                  backgroundColor: 'hsl(45 100% 50%)',
                  color: 'hsl(0 0% 10%)',
                  height: '56px'
                }}
              >
                LOGO
              </div>
            )}
          </div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Stat tiles + User info — right-aligned */}
          <div className="flex items-center divide-x divide-border overflow-x-auto min-w-0 scrollbar-none">
            {/* Stat tiles */}
            <div className="flex items-center gap-2 px-5">
              <Users className="w-4.5 h-4.5 text-primary shrink-0" />
              <span className="text-base font-semibold text-foreground tabular-nums">{currentOccupancy}</span>
              <span className="text-sm text-muted-foreground">On Site</span>
            </div>
            <div className="flex items-center gap-2 px-5">
              <ShieldAlert className="w-4.5 h-4.5 text-primary shrink-0" />
              <span className={`text-base font-semibold tabular-nums ${openIncidentsCount > 0 ? 'text-red-400' : 'text-foreground'}`}>
                {openIncidentsCount}
              </span>
              <span className="text-sm text-muted-foreground">Open Incidents</span>
            </div>
            <div className="flex items-center gap-2 px-5">
              <Activity className="w-4.5 h-4.5 text-primary shrink-0" />
              <span className={`text-base font-semibold ${siteStatus === 'hazard' ? 'text-red-400' : 'text-green-400'}`}>
                {getStatusText()}
              </span>
              <span className="text-sm text-muted-foreground">Site Status</span>
            </div>

            {/* User info */}
            <div className="flex items-center gap-2 pl-5">
              <div className="text-right">
                <div className="text-base font-medium text-foreground">
                  {user?.first_name || user?.email?.split('@')[0]} {user?.surname || ''}
                </div>
                <div className="text-sm text-primary capitalize">{user?.role}</div>
              </div>
              {onHelpClick && (
                <button
                  onClick={onHelpClick}
                  className="p-1.5 rounded hover:bg-secondary transition-colors"
                  title="Help & User Guide"
                >
                  <HelpCircle className="w-4 h-4 text-muted-foreground" />
                </button>
              )}
            </div>
          </div>{/* end right-aligned group */}
        </div>{/* end outer flex */}
      </header>

    {/* ── Hazard banner — in-flow below header ── */}
    {siteStatus === 'hazard' && latestHazard && (
      <button
        onClick={() => {
          if (totalHazardCount > 1) {
            setShowIncidentsModal(true);
          } else {
            navigate('/app/incidents');
          }
        }}
        className="w-full flex items-center gap-3 px-6 py-2 bg-red-600 border-b border-red-700 hover:brightness-110 transition-all opacity-80 hover:opacity-100 cursor-pointer"
      >
        <AlertTriangle className="w-4 h-4 text-white flex-shrink-0" />
        <div className="flex-1 min-w-0 flex items-center gap-2">
          <span className="text-xs font-bold text-white uppercase">{latestHazard.severity} Severity</span>
          <span className="text-xs text-red-100">•</span>
          <span className="text-xs text-white truncate">{latestHazard.title || latestHazard.category || 'Safety Incident'}</span>
          {latestHazard.location && (
            <>
              <span className="text-xs text-red-100">•</span>
              <span className="text-xs text-white truncate">{latestHazard.location}</span>
            </>
          )}
        </div>
        {totalHazardCount > 1 && (
          <span className="bg-red-700 text-white text-xs font-bold px-2 py-1 rounded">+{totalHazardCount - 1} more</span>
        )}
        <span className="text-xs text-red-100 flex-shrink-0">Click for details</span>
      </button>
    )}

    {/* All Incidents Modal */}
    <Dialog open={showIncidentsModal} onOpenChange={setShowIncidentsModal}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <AlertTriangle className="w-6 h-6 text-red-600" />
            Active Site Incidents ({totalHazardCount})
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-3 mt-4">
          {allHazards.map((incident, index) => (
            <div 
              key={incident.id}
              className="border-2 border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  {/* Severity Badge */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`${getSeverityColor(incident.severity)} text-white text-xs font-bold uppercase px-2 py-1 rounded`}>
                      {incident.severity} Severity
                    </span>
                    <span className={`text-xs px-2 py-1 rounded ${
                      incident.status === 'open' ? 'bg-red-100 text-red-800' : 
                      incident.status === 'investigating' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {incident.status?.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>

                  {/* Incident Title */}
                  <h3 className="font-semibold text-lg mb-1">
                    {incident.title || 'Untitled Incident'}
                  </h3>

                  {/* Category Badge */}
                  {incident.category && (
                    <span className="inline-block text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded mb-2">
                      {incident.category.charAt(0).toUpperCase() + incident.category.slice(1)}
                    </span>
                  )}

                  {/* Location */}
                  {incident.location && (
                    <p className="text-sm text-gray-600 mb-2">
                      📍 {incident.location}
                    </p>
                  )}

                  {/* Description */}
                  {incident.description && (
                    <p className="text-sm text-gray-700 mb-2">
                      {incident.description}
                    </p>
                  )}

                  {/* Metadata */}
                  <div className="flex items-center gap-4 text-xs text-gray-500 mt-2">
                    <span>Reported: {formatDate(incident.created_at)}</span>
                    {incident.reporter_name && (
                      <span>By: {incident.reporter_name}</span>
                    )}
                  </div>
                </div>

                {/* Quick Action */}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setShowIncidentsModal(false);
                    navigate(ROUTES.APP.INCIDENTS);
                  }}
                >
                  View Full Details
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Footer with action */}
        <div className="mt-6 pt-4 border-t flex justify-between items-center">
          <p className="text-sm text-gray-600">
            Click "View Full Details" to manage any incident
          </p>
          <Button
            onClick={() => setShowIncidentsModal(false)}
            variant="secondary"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default DepotHeader;
